import type { SearchCitiesParams } from './actions';

/**
 * Shared query parameters for the "type a city name" search, used by every autocomplete that calls
 * `searchCitiesAction` (the showtimes-finder SearchBar and the web app's LocationSelectorModal).
 *
 * CIN-14731. Two things here are load-bearing:
 *
 *  - `sort: 'population', order: 'desc'`. Without an explicit sort the API falls back to
 *    `sort=id`, and geonames ids are allocated in per-country blocks (BE 17xx…, CH 19xx…,
 *    SE 26xx…, DE 28xx…) — so the "unsorted" list is really sorted by an arbitrary country
 *    order, which put every Belgian and Swedish match ahead of Halle (Saale). Population
 *    descending puts the city the user most likely meant first. The API orders NULL populations
 *    last for this sort; before that fix `order=desc` was *worse* than no sort at all, because
 *    PostgreSQL defaults to NULLS FIRST and most geonames rows have no population.
 *
 *  - No `latitude`/`longitude`. The API makes distance the PRIMARY sort whenever coordinates are
 *    present and demotes `sort` to a tiebreaker, which ranks the nearest hamlet above the city
 *    that was typed. Adding coordinates here would silently undo the ordering above; that
 *    regression is guarded by test/city-search-sort.test.mjs.
 */
export const CITY_SEARCH_SORT_PARAMS = {
    sort: 'population',
    order: 'desc'
} as const satisfies Pick<SearchCitiesParams, 'sort' | 'order'>;

/**
 * Rows fetched per page of the autocomplete. The API's default of 10 meant the list ran out of
 * content every ten rows, and each refill is a round trip — long enough that a user scrolling
 * quickly hit the bottom of the container and the scroll chained to the page behind it.
 * The API caps perPage at 100 (ListInputFilterAbstract::MAX_PER_PAGE).
 */
export const CITY_SEARCH_PER_PAGE = 25;

/**
 * The countries Cineamo serves showings in — the Operating Countries (see CONTEXT.md). The API's
 * city search defaults to exactly these (CIN-15157), so no call site sends a country; this copy
 * exists only to decide when the auto-detect lookup is pointless. Mirrors
 * `CountryLanguageMap::cinemaOperatingCountries()` in the backend.
 */
export const OPERATING_COUNTRIES = ['DE', 'AT', 'CH'] as const;

/**
 * Whether the visitor's country is known and is not an Operating Country. The IP/GPS auto-detect
 * skips its nearest-city lookup then: with the search limited to DACH, a visitor in Stockholm would
 * get "Flensburg" filled in. An unknown country (failed IP lookup) is not outside — the nearest
 * Operating Country city is still the best guess.
 */
export function isOutsideOperatingCountries(country: string | null | undefined): boolean {
    if (!country) {
        return false;
    }
    return !(OPERATING_COUNTRIES as readonly string[]).includes(country.toUpperCase());
}

/**
 * How far ahead of the scroll container's bottom edge the next page starts loading. Paired with
 * passing the container itself as the IntersectionObserver root — without a root the observer
 * falls back to the viewport, so the sentinel only counts as visible once the user has already
 * reached the bottom and there is nothing left to scroll.
 */
export const CITY_SEARCH_PREFETCH_ROOT_MARGIN = '200px';
